import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import api from "@/lib/api";

const initial = { name: "", email: "", phone: "", organization: "", role: "", brand_interest: "both", message: "" };

export default function B2BLeadCapture() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please provide your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "b2b_form" });
      setDone(true);
      toast.success("Thank you — our partnerships team will reach out shortly.");
      setForm(initial);
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full bg-transparent border-0 border-b-2 border-pharma-soft/40 rounded-none py-4 px-1 text-base md:text-lg text-stone placeholder:text-pharma-soft/70 focus-visible:ring-0 focus:border-stone transition-colors";

  return (
    <section id="connect" className="px-4 md:px-12 my-24 md:my-32 scroll-mt-24">
      <div className="relative overflow-hidden rounded-[2rem] bg-pharma text-stone px-6 md:px-16 py-20 md:py-28">
        <img
          src="https://images.pexels.com/photos/6762869/pexels-photo-6762869.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="absolute right-0 top-0 h-full w-full lg:w-1/2 object-cover opacity-15 lg:opacity-40 grayscale mix-blend-luminosity pointer-events-none"
        />
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div className="flex flex-col gap-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-pharma-soft">For Doctors & Clinics</span>
            <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight">
              <MaskedLines lines={["Partner with", "OneKind."]} />
            </h2>
            <Reveal delay={0.1}>
              <p className="font-sans text-base md:text-lg text-stone/70 leading-relaxed max-w-md">
                Request our clinical dossiers, pricing and samples. Share your details and our
                partnerships team will connect with you directly.
              </p>
            </Reveal>
            <div className="flex gap-8 mt-4 border-t border-stone/15 pt-6 font-mono text-xs text-stone/60">
              <div><span className="block text-2xl font-serif text-stone">Doctor</span>Trusted</div>
              <div><span className="block text-2xl font-serif text-stone">2</span>Brands</div>
              <div><span className="block text-2xl font-serif text-stone">100%</span>Transparent</div>
            </div>
          </div>

          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="lead-success"
              className="flex flex-col justify-center gap-4 min-h-[300px]"
            >
              <span className="font-serif text-4xl">Received.</span>
              <p className="font-sans text-stone/70 max-w-sm">
                Thank you for your interest in OneKind Healthcare. We&apos;ll be in touch within one business day.
              </p>
              <button onClick={() => setDone(false)} className="self-start font-mono text-xs uppercase tracking-widest text-pharma-soft hover:text-stone transition-colors mt-2">
                Submit another →
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-6 w-full" data-testid="b2b-lead-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input className={field} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="lead-name" />
                <Input className={field} placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="lead-phone" />
              </div>
              <Input className={field} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="lead-email" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input className={field} placeholder="Clinic / Organisation" value={form.organization} onChange={set("organization")} data-testid="lead-org" />
                <Input className={field} placeholder="Role (e.g. Dermatologist)" value={form.role} onChange={set("role")} data-testid="lead-role" />
              </div>

              <Select value={form.brand_interest} onValueChange={(v) => setForm({ ...form, brand_interest: v })}>
                <SelectTrigger data-testid="lead-brand-select" className="w-full bg-transparent border-0 border-b-2 border-pharma-soft/40 rounded-none py-4 px-1 text-base md:text-lg text-stone focus:ring-0 focus:border-stone">
                  <SelectValue placeholder="Brand of interest" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both brands</SelectItem>
                  <SelectItem value="onekind">OneKind — Pharma & Wellness</SelectItem>
                  <SelectItem value="oneskin">One Skin — Skincare</SelectItem>
                </SelectContent>
              </Select>

              <Textarea className={field} placeholder="Tell us what you're looking for" value={form.message} onChange={set("message")} data-testid="lead-message" />

              <button
                type="submit"
                disabled={submitting}
                data-testid="b2b-submit-button"
                className="self-start rounded-full px-10 py-4 bg-stone text-ink text-xs uppercase tracking-widest font-sans hover:bg-derma hover:text-stone transition-colors disabled:opacity-50 mt-2"
              >
                {submitting ? "Sending…" : "Request Access"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
